/**
 * Carries a signed-in customer's "Do Not Sell or Share" choice to their
 * account (POST /api/privacy/opt-out), so the server stops reporting their
 * bookings to ad measurement too. Runs on every page, signed in or not: a
 * browser that sends Global Privacy Control, or that opted out on
 * /privacy-choices, records it once per account (src/lib/privacyChoices.js).
 */
import { useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { syncOptOutToAccount, PRIVACY_CHOICE_EVENT } from '@/lib/privacyChoices';

export async function postPrivacyChoice(accessToken, { optedOut, source }) {
  const res = await fetch('/api/privacy/opt-out', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ opted_out: optedOut, source }),
  });
  return res.ok;
}

export function usePrivacyChoiceSync() {
  useEffect(() => {
    let cancelled = false;
    const run = (session) => {
      if (cancelled || !session?.user?.id || !session.access_token) return;
      syncOptOutToAccount({
        userId: session.user.id,
        send: (choice) => postPrivacyChoice(session.access_token, choice),
      }).catch(() => {});
    };
    const fromCurrentSession = () => {
      supabase.auth.getSession().then(({ data }) => run(data?.session)).catch(() => {});
    };
    fromCurrentSession();
    const { data } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') run(session);
    });
    window.addEventListener(PRIVACY_CHOICE_EVENT, fromCurrentSession);
    return () => {
      cancelled = true;
      data?.subscription?.unsubscribe?.();
      window.removeEventListener(PRIVACY_CHOICE_EVENT, fromCurrentSession);
    };
  }, []);
}
